import React, { useState } from 'react';
import Sidebar from './Sidebar';
import AdvancedSearch from './AdvancedSearch';
import Customer360 from './Customer360';
import { Quotation } from '../data/dummyData';
import { Search, CheckSquare, Square, Download, Mail } from 'lucide-react';

interface QuotationListProps {
  quotations: Quotation[];
  onQuotationSelect: (quotation: Quotation) => void;
  onBackToDashboard: () => void;
  onNavigate: (view: 'dashboard' | 'quotations' | 'analytics' | 'settings' | 'ai-assistant' | 'user-management') => void;
}

const QuotationList: React.FC<QuotationListProps> = ({ quotations, onQuotationSelect, onBackToDashboard, onNavigate }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showCustomer360, setShowCustomer360] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedQuotations, setSelectedQuotations] = useState<string[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const filteredQuotations = quotations.filter(quotation => {
    const matchesFilter = filter === 'all' || quotation.status.toLowerCase() === filter.toLowerCase();
    const matchesSearch = quotation.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quotation.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quotation.company.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Date filtering
    let matchesDate = true;
    if (dateFilter === 'custom' && startDate && endDate) {
      const quotationDate = new Date(quotation.received);
      const start = new Date(startDate);
      const end = new Date(endDate);
      matchesDate = quotationDate >= start && quotationDate <= end;
    } else if (dateFilter === 'today') {
      const today = new Date();
      const quotationDate = new Date(quotation.received);
      matchesDate = quotationDate.toDateString() === today.toDateString();
    } else if (dateFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const quotationDate = new Date(quotation.received);
      matchesDate = quotationDate >= weekAgo;
    } else if (dateFilter === 'month') {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      const quotationDate = new Date(quotation.received);
      matchesDate = quotationDate >= monthAgo;
    }
    
    return matchesFilter && matchesSearch && matchesDate;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'Acknowledged': return 'bg-yellow-100 text-yellow-800';
      case 'Quoted': return 'bg-purple-100 text-purple-800';
      case 'Won': return 'bg-green-100 text-green-800';
      case 'Lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'Email': return '📧';
      case 'WhatsApp': return '💬';
      case 'Phone': return '📞';
      case 'Web': return '🌐';
      default: return '📄';
    }
  };

  const handleSearchResults = (results: Quotation[]) => {
    // This would update the displayed quotations
    console.log('Search results:', results);
  };

  const handleCustomerClick = (quotation: Quotation) => {
    const customer = {
      name: quotation.customerName,
      company: quotation.company,
      email: `${quotation.customerName.toLowerCase().replace(' ', '.')}@${quotation.company.toLowerCase().replace(' ', '')}.com`,
      phone: '+1-555-0123',
      location: quotation.origin,
      totalQuotations: quotations.filter(q => q.customerName === quotation.customerName).length,
      wonQuotations: quotations.filter(q => q.customerName === quotation.customerName && q.status === 'Won').length,
      totalValue: quotations.filter(q => q.customerName === quotation.customerName && q.status === 'Won')
        .reduce((sum, q) => sum + (q.estimatedValue || 0), 0),
      avgResponseTime: 2.3,
      lastInteraction: quotation.received,
      quotations: quotations.filter(q => q.customerName === quotation.customerName)
    };
    setSelectedCustomer(customer);
    setShowCustomer360(true);
  };

  const handleSelectQuotation = (quotationId: string) => {
    setSelectedQuotations(prev => 
      prev.includes(quotationId) 
        ? prev.filter(id => id !== quotationId)
        : [...prev, quotationId]
    );
    setShowBulkActions(selectedQuotations.length > 0);
  };

  const handleSelectAll = () => {
    if (selectedQuotations.length === filteredQuotations.length) {
      setSelectedQuotations([]);
    } else {
      setSelectedQuotations(filteredQuotations.map(q => q.id));
    }
    setShowBulkActions(selectedQuotations.length > 0);
  };

  const handleBulkAction = (action: string) => {
    console.log(`Bulk ${action} on quotations:`, selectedQuotations);
    // Implement bulk actions
    setSelectedQuotations([]);
    setShowBulkActions(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar currentView="quotations" onNavigate={(view) => {
        if (view === 'dashboard') {
          onBackToDashboard();
        } else {
          onNavigate(view);
        }
      }} />

      {/* Main Content */}
      <div className="flex-1 ml-64">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Quotation Inbox</h1>
              <p className="text-gray-600 mt-1">Manage and track all quotation requests</p>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setShowAdvancedSearch(true)}
                className="btn-secondary flex items-center space-x-2"
              >
                <Search className="w-4 h-4" />
                <span>Advanced Search</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Search */}
        <div className="card mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-wrap gap-2">
              {['all', 'new', 'acknowledged', 'quoted', 'won', 'lost'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === status
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Date Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Date:</label>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
              
              {/* Custom Date Range */}
              {dateFilter === 'custom' && (
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}
              
              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search quotations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-400">🔍</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {showBulkActions && (
          <div className="card mb-6 bg-primary-50 border-primary-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-primary-700">
                  {selectedQuotations.length} quotation{selectedQuotations.length !== 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleBulkAction('assign')}
                    className="px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 transition-colors"
                  >
                    Assign
                  </button>
                  <button
                    onClick={() => handleBulkAction('export')}
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 transition-colors flex items-center space-x-1"
                  >
                    <Download className="w-3 h-3" />
                    <span>Export</span>
                  </button>
                  <button
                    onClick={() => handleBulkAction('email')}
                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors flex items-center space-x-1"
                  >
                    <Mail className="w-3 h-3" />
                    <span>Email</span>
                  </button>
                  <button
                    onClick={() => handleBulkAction('archive')}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
                  >
                    Archive
                  </button>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedQuotations([]);
                  setShowBulkActions(false);
                }}
                className="text-primary-600 hover:text-primary-800 text-sm"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {/* Quotations Table */}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button onClick={handleSelectAll} className="flex items-center">
                      {selectedQuotations.length === filteredQuotations.length ? (
                        <CheckSquare className="w-4 h-4 text-primary-600" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Route</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weight</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredQuotations.map((quotation) => (
                  <tr key={quotation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectQuotation(quotation.id);
                        }}
                        className="flex items-center"
                      >
                        {selectedQuotations.includes(quotation.id) ? (
                          <CheckSquare className="w-4 h-4 text-primary-600" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => onQuotationSelect(quotation)}>
                      <div className="flex items-center">
                        <span className="text-lg mr-2">{getChannelIcon(quotation.channel)}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-900 max-w-xs truncate">{quotation.subject}</div>
                          <div className="text-sm text-gray-500">{quotation.company}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleCustomerClick(quotation)}
                        className="text-sm text-primary-600 hover:text-primary-800 hover:underline"
                      >
                        {quotation.customerName}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{quotation.origin}</div>
                      <div className="text-sm text-gray-500">→ {quotation.destination}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{quotation.serviceType}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{quotation.weight}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quotation.status)}`}>
                          {quotation.status}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(quotation.priority)}`}>
                          {quotation.priority}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{new Date(quotation.received).toLocaleDateString()}</div>
                      <div className="text-sm text-gray-500">{new Date(quotation.received).toLocaleTimeString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{quotation.assignedTo}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => onQuotationSelect(quotation)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredQuotations.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No quotations found</h3>
            <p className="text-gray-600">Try adjusting your filters or search terms</p>
          </div>
        )}
      </div>
      </div>

      {/* Modals */}
      {showAdvancedSearch && (
        <AdvancedSearch
          quotations={quotations}
          onSearchResults={handleSearchResults}
          onClose={() => setShowAdvancedSearch(false)}
        />
      )}

      {showCustomer360 && selectedCustomer && (
        <Customer360
          customer={selectedCustomer}
          onClose={() => {
            setShowCustomer360(false);
            setSelectedCustomer(null);
          }}
        />
      )}
    </div>
  );
};

export default QuotationList;
